function EventEntry() {
    this.children = {}
    this.handlers = []
    this.allBelow = []
}

EventEntry.prototype.getChild = function getChild(key) {
    const child = this.children[key]
    if (!child) {
        const result = new EventEntry()
        this.children[key] = result
        return result
    }
    return child
}

EventEntry.prototype.getExisting = function getExisting(key) {
    return this.children[key]
}

EventEntry.prototype.getAll = function getAll() {
    return (this._all = this._all || new EventEntry())
}

/**
 * @callback HandlePreparer
 *
 * @param {Array<Function>} handlers - the handlers being used
 * @return an updated array or the original array sorted
 */

/**
 * @interface ConstructorParams
 * @property {string} [delimiter=.] - a character which delimits parts of an event pattern
 * @property {string} [wildcard=*] - a wildcard indicator used to handle any parts of a pattern
 * @property {string} [separator=,] - a character to separate multiple events in the same pattern
 * @property {HandlePreparer} [prepareHandlers=v=>v] - a function to modify the handlers just before raising,
 * this is the combined set of all of the handlers that will be raised.
 * @property {HandlePreparer} [storeHandlers=v=>v] - a function to modify or sort the handlers before storing,

 */

/**
 * Event emitter with wild card support and delimited entries.
 */
class Events {
    /**
     * Constructs an event emitter
     * @param {ConstructorParams} [props] - parameters to configure the emitter
     */
    constructor({ delimiter = ".", wildcard = "*", storeHandlers } = {}) {
        this.delimiter = delimiter
        this.wildcard = wildcard = wildcard === true ? "*" : wildcard
        this.cache = new Map()
        this.doubleWild = `${wildcard}${wildcard}`
        this._events = new EventEntry()
        this.storeHandlers = storeHandlers
        this.once = this.once.bind(this)
    }

    /**
     * Adds an event listener with wildcards etc
     * @instance
     * @memberOf Events
     * @param {string|Array<string>} name - the event pattern to handle
     * @param {Function} handler - the handler for the pattern
     */
    on(name, handler) {
        if (!handler) return
        if (Array.isArray(name)) {
            name.forEach((name) => this.on(name, handler))
            return
        }
        const parts = name.split(this.delimiter)
        let scan = this._events

        const { storeHandlers } = this
        const { wildcard } = this
        const { doubleWild } = this
        for (let i = 0, l = parts.length; i < l; i++) {
            const part = parts[i]
            if (part === wildcard) {
                scan = scan.getAll()
            } else if (part === doubleWild) {
                scan.allBelow.push(handler)
                scan.allBelow = storeHandlers ? storeHandlers(scan.allBelow) : scan.allBelow
            } else {
                scan = scan.getChild(part)
            }
        }
        scan.handlers.push(handler)
        scan.handlers = storeHandlers ? storeHandlers(scan.handlers) : scan.handlers
    }

    /**
     * Add an event listener that will fire only once, if multiple
     * patterns are provided it will only fire on the first one
     * @param {string|Array<string>} name - the event pattern to listen for
     * @param {Function} handler - the function to invoke
     */
    once(name, handler) {
        const self = this
        self.on(name, process)

        function process(...params) {
            self.off(name, process)
            handler(...params)
        }

        return () => {
            self.off(name, process)
        }
    }

    removeAllListeners() {
        this._events = new EventEntry()
    }

    /**
     * Removes a listener from a pattern
     * @param {string|Array<string>} name- the pattern of the handler to remove
     * @param {Function} [handler] - the handler to remove, or all handlers
     */
    off(name, handler) {
        if (Array.isArray(name)) {
            name.forEach((name) => this.off(name, handler))
            return
        }
        const parts = name.split(this.delimiter)
        let scan = this._events
        const { wildcard } = this
        const { doubleWild } = this
        for (let i = 0, l = parts.length; scan && i < l; i++) {
            const part = parts[i]
            switch (part) {
                case wildcard:
                    scan = scan.getAll()
                    break
                case doubleWild: {
                    if (handler === undefined) {
                        scan.allBelow = []
                        return
                    }
                    const idx = scan.allBelow.indexOf(handler)
                    if (idx === -1) return
                    if (scan._iterating) {
                        scan.allBelow[idx] = null
                    } else {
                        scan.allBelow.splice(idx, 1)
                    }
                    return
                }
                default:
                    scan = scan.getExisting(part)
                    break
            }
        }
        if (!scan) return
        if (handler !== undefined) {
            const idx = scan.handlers.indexOf(handler)
            if (idx === -1) return
            if (scan._iterating) {
                scan.handlers[idx] = null
            } else {
                scan.handlers.splice(idx, 1)
            }
        } else {
            scan.handlers.length = 0
        }
    }

    /**
     * Emits an event synchronously
     * @param {string} event - the event to emit
     * @param {...params} params - the parameters to call the event with
     * @returns {Array<any>} - an array of the parameters the event was called with
     */
    emit(event, ...params) {
        const parts = event.split(this.delimiter)
        _emit(this._events, parts, 0, null, (fn) => {
            fn.apply({ event, context: params[0] }, params)
        })
        return params
    }

    /**
     * Emits events asynchronously, in order, sequentially
     * @param {string} event - the event to emit
     * @param {...params} params - the parameters to call the event with
     * @returns {Array<any>} - an array of the parameters the event was called with
     */
    async emitAsyncSequential(event, ...params) {
        const handlers = []
        const parts = event.split(this.delimiter)
        _emit(this._events, parts, 0, handlers)
        for (const handler of handlers) {
            // eslint-disable-next-line no-await-in-loop
            await handler.apply({ event }, params)
        }
        return params
    }

    /**
     * Emits events asynchronously, in parallel
     * @param {string} event - the event to emit
     * @param {...params} params - the parameters to call the event with
     * @returns {Array<any>} - an array of the parameters the event was called with
     */
    async emitAsync(event, ...params) {
        const handlers = []
        const parts = event.split(this.delimiter)
        _emit(this._events, parts, 0, null, (fn) => {
            handlers.push(Promise.resolve(fn.apply({ event, context: params[0] }, params)))
        })
        await Promise.all(handlers)
        return params
    }
}

function _emit(scan, parts, index, handlers, call) {
    for (; scan && index < parts.length; index++) {
        const { allBelow } = scan
        if (call) {
            scan._iterating = (scan._iterating || 0) + 1
            try {
                for (let i = 0, l = allBelow.length; i < l; i++) {
                    const handler = allBelow[i]
                    if (handler) {
                        call(handler)
                    }
                }
            } finally {
                if (--scan._iterating === 0) {
                    for (let i = allBelow.length - 1; i >= 0; i--) {
                        if (allBelow[i] === null) allBelow.splice(i, 1)
                    }
                }
            }
            if (scan._all) {
                _emit(scan.getAll(), parts, index + 1, handlers, call)
            }
            scan = scan.children[parts[index]]
        } else {
            for (let i = 0, l = allBelow.length; i < l; i++) {
                const handler = allBelow[i]
                if (handler) {
                    handlers.push(handler)
                }
            }
            if (scan._all) {
                _emit(scan.getAll(), parts, index + 1, handlers, call)
            }
            scan = scan.children[parts[index]]
        }
    }
    if (scan) {
        const currentHandlers = scan.handlers
        if (call) {
            scan._iterating = (scan._iterating || 0) + 1
            try {
                for (let i = 0, l = currentHandlers.length; i < l; i++) {
                    const currentHandler = currentHandlers[i]
                    if (currentHandler) {
                        call(currentHandler)
                    }
                }
            } finally {
                if (--scan._iterating === 0) {
                    for (let i = currentHandlers.length - 1; i >= 0; i--) {
                        if (currentHandlers[i] === null) {
                            currentHandlers.splice(i, 1)
                        }
                    }
                }
            }
        } else {
            for (let i = 0, l = currentHandlers.length; i < l; i++) {
                const currentHandler = currentHandlers[i]
                if (currentHandler) {
                    handlers.push(currentHandler)
                }
            }
        }
    }
}

Events.prototype.addEventListener = Events.prototype.on
Events.prototype.removeEventListener = Events.prototype.off
Events.prototype.addListener = Events.prototype.on
Events.prototype.removeListener = Events.prototype.off
export { Events }
